import React, { useState, useCallback } from "react";

// Pricing Plans Data
const hostingPlans = [
  {
    key: "single",
    title: "Single",
    subtitle: "A great solution for beginners",
    price: 150,
    websites: "Create 1 website",
    credits: "5 vibe coding credits",
    storage: "10 GB of storage for your files (SSD)",
    mailboxes: "1 mailbox per website - free for 1 year",
  },
  {
    key: "premium",
    title: "Premium",
    subtitle: "Everything you need to get started",
    price: 350,
    popular: true,
    websites: "Create up to 3 websites",
    credits: "15 vibe coding credits",
    storage: "20 GB of storage for your files (SSD)",
    mailboxes: "2 mailboxes per website - free for 1 year",
  },
  {
    key: "business",
    title: "Business",
    subtitle: "More tools and power for growth",
    price: 550,
    websites: "Create up to 50 websites",
    credits: "30 vibe coding credits",
    storage: "50 GB of world's fastest NVMe storage",
    mailboxes: "5 mailboxes per website - free for 1 year",
  },
  {
    key: "cloud_startup",
    title: "Cloud Startup",
    subtitle: "20X more power for your websites with Cloud hosting",
    price: 820,
    websites: "Create up to 100 websites",
    credits: "50 vibe coding credits",
    storage: "100 GB of world's fastest NVMe storage",
    mailboxes: "10 mailboxes per website - free for 1 year",
  },
];

const PricingCards = ({ onChoosePlan, selectedPlanKey: externalSelectedKey }) => {
  const [internalSelectedKey, setInternalSelectedKey] = useState("premium");
  
  const selectedPlanKey = externalSelectedKey !== undefined 
    ? externalSelectedKey 
    : internalSelectedKey;

  const handleChoosePlan = useCallback((plan) => {
    if (externalSelectedKey === undefined) {
      setInternalSelectedKey(plan.key);
    }
    onChoosePlan?.(plan);
  }, [onChoosePlan, externalSelectedKey]);

  const handlePlanSelect = useCallback((plan) => {
    handleChoosePlan(plan);
    
    const formElement = document.querySelector("#hosting-request-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [handleChoosePlan]);

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        /* Section Styles */
        .pricing-cards-section {
          padding: 80px 24px;
          background: linear-gradient(135deg, #ec6ab8 0%, #b457e9 48%, #7c3cff 100%);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }

        .pricing-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Header Styles */
        .pricing-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .pricing-title {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #ffe0f5 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 24px;
          letter-spacing: -0.02em;
        }

        .trust-badges {
          display: flex;
          justify-content: center;
          gap: clamp(16px, 3vw, 32px);
          flex-wrap: wrap;
        }

        .trust-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 40px;
          font-size: 0.875rem;
          color: #ffffff;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .trust-badge:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
        }

        /* Pricing Grid */
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 28px;
          margin-top: 20px;
        }

        /* Card Styles */
        .pricing-card {
          position: relative;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border-radius: 28px;
          padding: 32px 24px 40px;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          border: 1px solid rgba(255, 255, 255, 0.2);
          cursor: pointer;
        }

        .pricing-card:hover {
          transform: translateY(-8px);
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 25px 40px -12px rgba(0, 0, 0, 0.3);
          background: rgba(255, 255, 255, 0.15);
        }

        .pricing-card.selected {
          border: 2px solid #ffffff;
          background: rgba(255, 255, 255, 0.2);
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
        }

        .pricing-card.popular {
          transform: scale(1.02);
        }

        @media (max-width: 768px) {
          .pricing-card.popular {
            transform: scale(1);
          }
        }

        /* Popular Badge */
        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #ffd89b, #c7e9fb);
          padding: 6px 20px;
          border-radius: 40px;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 1px;
          color: #7c3cff;
          text-transform: uppercase;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        /* Card Content */
        .card-content {
          text-align: center;
        }

        .plan-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .plan-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 24px;
        }

        /* Price Styles */
        .plan-price {
          margin-bottom: 32px;
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;
        }

        .currency {
          font-size: 1.5rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }

        .price {
          font-size: 3.5rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.02em;
        }

        .period {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.7);
        }

        /* Button Styles */
        .choose-plan-btn {
          width: 100%;
          padding: 14px 24px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
          border: none;
          border-radius: 60px;
          font-size: 1rem;
          font-weight: 600;
          color: #7c3cff;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 28px;
        }

        .choose-plan-btn:hover {
          background: linear-gradient(135deg, #ffffff, #ffe0f5);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        .choose-plan-btn.selected {
          background: rgba(255, 255, 255, 0.3);
          border: 1px solid #ffffff;
          color: #ffffff;
          cursor: default;
        }

        .choose-plan-btn.selected:hover {
          transform: none;
          box-shadow: none;
        }

        /* Feature List */
        .feature-list {
          list-style: none;
          text-align: left;
          margin-top: 20px;
        }

        .feature-list li {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 0;
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.85);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .feature-list li:last-child {
          border-bottom: none;
        }

        .check-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          font-size: 0.75rem;
          color: #ffffff;
          flex-shrink: 0;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .pricing-cards-section {
            padding: 60px 20px;
          }
          
          .pricing-grid {
            gap: 24px;
          }
        }

        @media (max-width: 768px) {
          .pricing-cards-section {
            padding: 48px 16px;
          }
          
          .pricing-header {
            margin-bottom: 40px;
          }
          
          .trust-badges {
            gap: 12px;
          }
          
          .trust-badge {
            font-size: 0.75rem;
            padding: 6px 12px;
          }
          
          .pricing-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .pricing-card {
            padding: 28px 20px 32px;
          }
          
          .plan-title {
            font-size: 1.5rem;
          }
          
          .price {
            font-size: 2.75rem;
          }
          
          .choose-plan-btn {
            padding: 12px 20px;
            font-size: 0.9rem;
          }
          
          .feature-list li {
            font-size: 0.8rem;
            padding: 8px 0;
          }
        }

        @media (max-width: 480px) {
          .pricing-cards-section {
            padding: 40px 12px;
          }
          
          .pricing-title {
            font-size: 1.75rem;
          }
          
          .trust-badge {
            font-size: 0.7rem;
            padding: 5px 10px;
          }
          
          .pricing-card {
            padding: 24px 16px 28px;
          }
          
          .plan-price {
            margin-bottom: 24px;
          }
          
          .price {
            font-size: 2.25rem;
          }
          
          .choose-plan-btn {
            padding: 10px 16px;
            margin-bottom: 20px;
          }
        }

        /* Animation for cards */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pricing-card {
          animation: fadeInUp 0.6s ease forwards;
          opacity: 0;
        }

        .pricing-card:nth-child(1) { animation-delay: 0.1s; }
        .pricing-card:nth-child(2) { animation-delay: 0.2s; }
        .pricing-card:nth-child(3) { animation-delay: 0.3s; }
        .pricing-card:nth-child(4) { animation-delay: 0.4s; }
      `}</style>

      <section className="pricing-cards-section">
        <div className="pricing-container">
          <div className="pricing-header">
            <h1 className="pricing-title">Web Hosting Plans</h1>
            
            <div className="trust-badges">
              <span className="trust-badge">🛡️ 30-day money-back guarantee</span>
              <span className="trust-badge">🎧 24/7 support</span>
              <span className="trust-badge">↺ Cancel anytime</span>
            </div>
          </div>

          <div className="pricing-grid">
            {hostingPlans.map((plan, index) => (
              <div
                key={plan.key}
                className={`pricing-card ${
                  selectedPlanKey === plan.key ? "selected" : ""
                } ${plan.popular ? "popular" : ""}`}
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
              >
                {plan.popular && (
                  <div className="popular-badge">
                    <span>MOST POPULAR</span>
                  </div>
                )}
                
                <div className="card-content">
                  <h2 className="plan-title">{plan.title}</h2>
                  <p className="plan-subtitle">{plan.subtitle}</p>
                  
                  <div className="plan-price">
                    <span className="currency">₹</span>
                    <span className="price">{plan.price}</span>
                    <span className="period">/mo</span>
                  </div>
                  
                  <button 
                    className={`choose-plan-btn ${
                      selectedPlanKey === plan.key ? "selected" : ""
                    }`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {selectedPlanKey === plan.key ? "✓ Selected Plan" : "Choose Plan"}
                  </button>
                  
                  <ul className="feature-list">
                    <li>
                      <span className="check-icon">✓</span>
                      {plan.websites}
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      {plan.credits}
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      {plan.storage}
                    </li>
                    <li>
                      <span className="check-icon">✓</span>
                      {plan.mailboxes}
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingCards;